import { CalendarDate, DateValue } from '@internationalized/date'
import {
  Card,
  CardBody,
  CardHeader,
  RangeValue,
  Select,
  SelectItem,
  Tab,
  Tabs
} from '@nextui-org/react'
import { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import AccountManagement from '~/components/super-admin/AccountManagement'
import AllowedEmailDomains from '~/components/super-admin/AllowedEmailDomains'
import UsageEventsTab from '~/components/super-admin/UsageEventsTab'
import BillingPaymentsOverview from '~/components/super-admin/BillingPaymentsOverview'
import BlockedEmailDomains from '~/components/super-admin/BlockedEmailDomains'
import WhitelistedEmails from '~/components/super-admin/WhitelistedEmails'
import Dashboard from '~/components/super-admin/Dashboard'
import DataFootprint from '~/components/super-admin/DataFootprint'
import ScriptRunner from '~/components/super-admin/ScriptRunner'
import SecretsManager from '~/components/super-admin/SecretsManager'
import TasksTab from '~/components/super-admin/TaskTab'
import UserActivityLeaderboard from '~/components/super-admin/UserActivityLeaderboard'
import UserManagement from '~/components/super-admin/UserManagement'
import { AgentCommands, UsedCommandType } from '~/constants/commands'
import { CommandType } from '~/protos/agents'
import {
  getSettingsCampaignAgentType,
  updateSettingsCampaignAgentType
} from '~/services/superAdminService'
import { getMainAppUrl, isSuperAdminSubdomain } from '~/utils/utils'

// Types
export interface PreviewData {
  headers: string[]
  rows: string[][]
}

export interface DateRange {
  startDate: Date
  endDate: Date
}

export const getDefaultDateRange = (): RangeValue<DateValue> => {
  const now = new Date()
  const utcNow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  const sevenDaysAgo = new Date(utcNow.getTime() - 7 * 24 * 60 * 60 * 1000)
  return {
    start: new CalendarDate(
      sevenDaysAgo.getUTCFullYear(),
      sevenDaysAgo.getUTCMonth() + 1,
      sevenDaysAgo.getUTCDate()
    ),
    end: new CalendarDate(utcNow.getUTCFullYear(), utcNow.getUTCMonth() + 1, utcNow.getUTCDate())
  }
}

export const convertDateValueToDate = (dateValue: DateValue): Date => {
  return new Date(Date.UTC(dateValue.year, dateValue.month - 1, dateValue.day))
}

export const convertToUTC = (date: Date, isEnd = false): string => {
  if (isEnd) {
    return new Date(
      Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59, 999)
    ).toISOString()
  }
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0)
  ).toISOString()
}

export const downloadBlobAsFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

const SuperAdminPage = () => {
  const { tab } = useParams<{ tab: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const selectedTab = tab || 'dashboard'

  // Determine base path based on whether we're on the superadmin subdomain at root
  const isAtRoot = isSuperAdminSubdomain() && !location.pathname.startsWith('/super-admin')
  const basePath = isAtRoot ? '' : '/super-admin'

  const [agentType, setAgentType] = useState<CommandType>(
    CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION
  )
  const CampaignAgentTypeOptions = [
    CommandType.GENERATE_IMAGE,
    CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION
  ]

  const CampaignAgentTypeItems = CampaignAgentTypeOptions.map(option => {
    return { key: option.toString(), label: AgentCommands[option as UsedCommandType].label }
  })

  useEffect(() => {
    const fetchAgentType = async () => {
      try {
        const type = await getSettingsCampaignAgentType()
        if (!type) return
        setAgentType(type.agent_type as CommandType)
      } catch (error) {
        console.error('Failed to fetch agent type:', error)
      }
    }
    fetchAgentType()
  }, [])

  const handleAgentTypeChange = async (newValue: string) => {
    let newAgentType = CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION // default to the DataWeave Generator option
    switch (parseInt(newValue)) {
      case CommandType.GENERATE_IMAGE:
        newAgentType = CommandType.GENERATE_IMAGE
        break
      case CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION:
        newAgentType = CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION
        break
      default:
        break
    }
    setAgentType(newAgentType)
    try {
      await updateSettingsCampaignAgentType({ agentType: newAgentType })
    } catch (error) {
      console.error('Failed to update agent type:', error)
    }
  }

  const handleTabChange = (key: React.Key | null) => {
    if (key === null) return
    navigate(`${basePath}/${key}`, { replace: true })
  }

  return (
    <div className='flex flex-col h-screen bg-tokens-neutral-bg-grey-1'>
      {/* Header */}
      <header className='flex items-center justify-between px-6 py-3 bg-white border-b border-tokens-neutral-border-2 shrink-0'>
        <div className='flex items-center gap-4'>
          <a
            href={getMainAppUrl()}
            target='_blank'
            rel='noopener noreferrer'
            className='flex items-center gap-2 text-tokens-neutral-text-secondary hover:text-tokens-neutral-text-primary transition-colors'
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 19l-7-7 7-7'
              />
            </svg>
            <span className='text-sm font-medium'>Back to App</span>
          </a>
          <div className='w-px h-6 bg-tokens-neutral-border-1' />
          <h1 className='text-lg font-semibold text-tokens-neutral-text-primary'>Super Admin</h1>
        </div>
      </header>

      {/* Main content */}
      <div className='flex-1 overflow-auto px-6 py-4'>
        <div className='h-full'>
          <Tabs
            selectedKey={selectedTab}
            onSelectionChange={handleTabChange}
            classNames={{
              base: 'w-full',
              tabList:
                'gap-0 w-full bg-white rounded-lg border border-tokens-neutral-border-2 p-1 mb-4',
              cursor: 'bg-tokens-primary-3',
              tab: 'px-4 py-2 text-sm font-medium',
              tabContent:
                'group-data-[selected=true]:text-tokens-primary-2 text-tokens-neutral-text-secondary'
            }}
          >
            <Tab key='dashboard' title='Dashboard'>
              <Dashboard />
            </Tab>
            <Tab key='settings' title='Settings'>
              <div className='flex flex-col gap-4'>
                <Card radius='sm' shadow='sm' className='bg-white'>
                  <CardHeader className='p-4 border-b border-tokens-neutral-border-2'>
                    <h2 className='text-lg font-semibold'>Campaign Settings</h2>
                  </CardHeader>
                  <CardBody className='p-4'>
                    <div className='flex items-center justify-between'>
                      <label className='text-base font-medium text-tokens-neutral-text-primary'>
                        Campaign Agent Type
                      </label>
                      <Select
                        defaultSelectedKeys={[agentType.toString()]}
                        items={CampaignAgentTypeItems}
                        onChange={e => handleAgentTypeChange(e.target.value)}
                        className='w-64'
                        size='sm'
                      >
                        {CampaignAgentTypeItems.map(option => (
                          <SelectItem key={option.key}>{option.label}</SelectItem>
                        ))}
                      </Select>
                    </div>
                  </CardBody>
                </Card>
                <AllowedEmailDomains />
                <BlockedEmailDomains />
                <WhitelistedEmails />
              </div>
            </Tab>
            <Tab key='account-management' title='Account Management'>
              <AccountManagement />
            </Tab>
            <Tab key='billing-payments' title='Billing & Payments'>
              <BillingPaymentsOverview />
            </Tab>
            <Tab key='user-management' title='User Management'>
              <UserManagement />
            </Tab>
            <Tab key='usage-events' title='Usage Events'>
              <UsageEventsTab />
            </Tab>
            <Tab key='tasks' title='Conversations / Tasks'>
              <TasksTab />
            </Tab>
            <Tab key='activity-leaderboard' title='Activity Leaderboard'>
              <UserActivityLeaderboard />
            </Tab>
            <Tab key='data-footprint' title='Data Footprint'>
              <DataFootprint />
            </Tab>
            <Tab key='scripts' title='Scripts'>
              <ScriptRunner />
            </Tab>
            <Tab key='secrets' title='Secrets'>
              <SecretsManager />
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  )
}

const Page = () => {
  const location = useLocation()
  const { tab } = useParams<{ tab?: string }>()

  // On superadmin subdomain, the parent route /:tab/* already extracts the tab param
  // In this case, render SuperAdminPage directly without nested routing
  if (isSuperAdminSubdomain() && tab) {
    return <SuperAdminPage />
  }

  // For root path on superadmin subdomain, redirect to /dashboard
  if (isSuperAdminSubdomain() && location.pathname === '/') {
    return <Navigate to='/dashboard' replace />
  }

  // For /super-admin/* paths (non-subdomain access), use nested routing
  const dashboardPath = '/super-admin/dashboard'

  return (
    <Routes>
      <Route index path='' element={<Navigate to={dashboardPath} replace />} />
      {/* Use /* to allow nested routes within tabs */}
      <Route path=':tab/*' element={<SuperAdminPage />} />
    </Routes>
  )
}

export default Page
